/**
 * Which CMS pages the App Router serves. Read by `src/middleware.ts`, so everything here has
 * to run on the edge runtime — `fetch` and nothing else.
 *
 * The list is fetched whole rather than one slug at a time. Measured against production, a
 * single-page lookup and the full list cost the same (~140 ms cold, ~35 ms on a warm
 * connection) because almost all of it is connection setup — so per-slug lookups would just
 * mean every new path pays that once, instead of only the first request after a cold start.
 *
 * Swapping Strapi for Vercel Edge Config later means replacing `fetchV2Slugs` and nothing
 * else; the middleware never talks to Strapi directly.
 */
import { type Locale, toStrapiLocale } from "@lib/i18n/locale";

const STRAPI_URL =
  process.env.NEXT_PUBLIC_STRAPI_BASE_URL || "http://localhost:1337";

const TTL_MS = 60_000;
const PAGE_LIMIT = 100;

/** The slugs of the v2 pages, per locale. */
export type V2Slugs = Map<string, Set<string>>;

interface Cache {
  slugs: V2Slugs;
  at: number;
}

let cache: Cache | null = null;
let inFlight: Promise<void> | null = null;

/**
 * The homepage is the awkward one: Strapi stores its slug as `"/"`, while a request for it
 * arrives as pathname `"/"` and reduces to the empty string. Both sides go through the two
 * functions below so the empty string is a named value rather than something that falls out
 * of a regular expression — it is invisible in a log, which makes it easy to mistake for
 * null.
 */
export const HOME_SLUG = "";

/**
 * Every App Router route lives under this static prefix rather than at its public path. The
 * Pages Router's client router cannot see App Router routes, and its `[locale]/[[...slug]]`
 * matches any path; the client router filter in next.config.js only recognises an App Router
 * route by a static leading segment, and without it a link would render the CMS page instead.
 */
export const APP_ROUTER_PREFIX = "/v2";

export const toAppRouterPath = (locale: Locale, slug: string) =>
  `${APP_ROUTER_PREFIX}/${locale}${slug ? `/${slug}` : ""}`;

// Must match the App Router rewrites in next.config.js.
const APP_ROUTER_REWRITES = [/^(?:\/(?:en|immo))?\/learning-center(?:[/?#]|$)/];

export const isRewrittenToAppRouter = (href: string) =>
  APP_ROUTER_REWRITES.some((route) => route.test(href));

/** The routing decision, kept out of the middleware so it can be tested. */
export const shouldUseAppRouter = ({
  locale,
  slug,
  slugs,
}: {
  locale: Locale;
  slug: string;
  slugs: V2Slugs | null;
}) => slugs?.get(toStrapiLocale(locale))?.has(slug) === true;

/** Path (`/ueber-uns/`) to slug (`ueber-uns`); `/` becomes `HOME_SLUG`. */
export const toSlug = (pathname: string) => pathname.replace(/^\/+|\/+$/g, "");

/** Slug back to the value Strapi stores, which for the homepage is `"/"`. */
const toStrapiSlug = (slug: string) => (slug === HOME_SLUG ? "/" : slug);

const listUrl = () =>
  `${STRAPI_URL}/api/pages?${new URLSearchParams({
    "filters[renderer][$eq]": "v2",
    "fields[0]": "slug",
    "fields[1]": "locale",
    "pagination[limit]": String(PAGE_LIMIT),
    locale: "all",
  })}`;

const fetchV2Slugs = async (): Promise<V2Slugs> => {
  const response = await fetch(listUrl(), {
    headers: { accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error(`Strapi answered ${response.status}`);
  }

  const body = (await response.json()) as {
    data?: {
      attributes?: { slug?: string | null; locale?: string | null };
    }[];
  };

  const slugs: V2Slugs = new Map();

  for (const { attributes } of body.data ?? []) {
    if (typeof attributes?.slug !== "string" || !attributes.locale) {
      continue;
    }

    const localeSlugs = slugs.get(attributes.locale) ?? new Set<string>();
    localeSlugs.add(toSlug(attributes.slug));
    slugs.set(attributes.locale, localeSlugs);
  }

  return slugs;
};

/** The cached list, or `null` while nothing has been fetched yet. Never blocks. */
export const getCachedV2Slugs = () => cache?.slugs ?? null;

export const isStale = () => !cache || Date.now() - cache.at > TTL_MS;

/**
 * Refreshes the cache. Safe to call on every request: concurrent calls share one fetch, and
 * a failure keeps the previous list rather than emptying it — a Strapi outage must not move
 * every page back to the Pages Router at once.
 */
export const refreshV2Slugs = async () => {
  if (inFlight) {
    return inFlight;
  }

  inFlight = fetchV2Slugs()
    .then((slugs) => {
      cache = { slugs, at: Date.now() };
    })
    .catch(() => {
      if (cache) {
        cache.at = Date.now();
      }
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

/**
 * Asks Strapi about one slug, ignoring the cache and the publication state. Preview shows
 * unpublished pages, which are by definition absent from the cached list.
 */
export const isV2SlugPreview = async (slug: string, locale: Locale) => {
  try {
    const response = await fetch(
      `${STRAPI_URL}/api/pages?${new URLSearchParams({
        "filters[slug][$eq]": toStrapiSlug(toSlug(slug)),
        "fields[0]": "renderer",
        publicationState: "preview",
        locale: toStrapiLocale(locale),
      })}`,
      { headers: { accept: "application/json" } },
    );

    if (!response.ok) {
      return false;
    }

    const body = (await response.json()) as {
      data?: { attributes?: { renderer?: string | null } }[];
    };

    // Any of them, not the first: a slug can have more than one entry — typically a
    // published legacy page alongside the unpublished v2 one that is being previewed — and
    // Strapi returns them in id order, so the first is the old page.
    return (body.data ?? []).some(
      (entry) => entry.attributes?.renderer === "v2",
    );
  } catch {
    return false;
  }
};
